import { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useEffect, useState } from 'react'
import { Button, CheckPicker, Form } from 'rsuite'
import { mergeLeft } from 'merge-left-utils'

import { prepareDeviceFilter } from '@@partner:helpers/device'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'
import { commonFieldChange } from '@@partner:helpers/util'

import './DevicesFilter.scss'
import { useLazyFetchShops } from '@@partner:graphql/query/shop/shops.gql'
import { RsuiteItemDataType } from '@@partner:types:app'
import { getSearchableShops } from '@@partner:helpers/order'
import { renderLazyPickerMenu } from '@components/common/pickers'
import { useFetchShopDeviceFilters } from '@@partner:graphql/query/device/shopDeviceFilters.gql'

type DevicesFilterProps = {
  onFilterChanged: (filters: NestedFilter) => void
}

export type DevicesFiltersType = {
  id: GQL_ID
  uuid: string[]
  hardwareId: string[]
  name: string
  type?: string[]
  model?: string[]
  os?: string[]
  osVersion?: string[]
  appVersion?: string[]
  shopId?: GQL_ID[]
}

const defaultFilter: DevicesFiltersType = {
  id: '',
  uuid: [],
  hardwareId: [],
  name: '',
  type: undefined,
  model: undefined,
  os: undefined,
  osVersion: undefined,
  appVersion: undefined,
  shopId: []
}

export default function DevicesFilter ({
  onFilterChanged
}: DevicesFilterProps) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  const [ state, setState ] = React.useState<DevicesFiltersType>(defaultFilter)

  const [ shops, setShops ] = useState<RsuiteItemDataType[]>([])

  const [ fetchShops, { data, loading } ] = useLazyFetchShops()
  const { data: filtersData, loading: loadingFiltersData } = useFetchShopDeviceFilters()

  useEffect(() => {
    const searchParams = getSearchParams()
    const newState: DevicesFiltersType = mergeLeft(state, searchParams)
    setState(newState)
    onFilterChanged(prepareDeviceFilter(newState))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!loading && data) {
      setShops(getSearchableShops(data.shops))
    }
  }, [ loading, data ])


  const handleFormSubmit = useCallback(() => {
    setSearchParams(state)
    onFilterChanged(prepareDeviceFilter(state))
  }, [ onFilterChanged, state, setSearchParams ])

  const handleFieldChange = commonFieldChange(setState)
  
  const handleFormChange = (formValue: Partial<DevicesFiltersType>) => {
    setState((state) => mergeLeft(state, formValue))
  }

  const getSearchableValues = (values?: string[]) => {
    if (!values) {
      return []
    }

    return values.map((value) => ({
      value,
      label: value
    })
    )
  }
  

  return (
    <Form
      formValue={state}
      className='DevicesFilter'
      onSubmit={handleFormSubmit}
      onChange={handleFormChange}
    >
      <Form.Group className='DevicesFilter__Filters'>
        <Form.Control 
          name="id"
          placeholder='ID'
          className='DevicesFilter__Filters__Input'
        />
        <Form.Control
          name='uuid'
          placeholder='UUID'
          className='DevicesFilter__Filters__Input'
        />
        <Form.Control
          name='hardwareId'
          placeholder='Hardware ID'
          className='DevicesFilter__Filters__Input'
        />
        <Form.Control
          name='name'
          placeholder='Name'
          className='DevicesFilter__Filters__Input'
        />
        <CheckPicker
          value={state.type}
          data={getSearchableValues(filtersData?.shopDeviceFilters.deviceTypes) ?? []}
          searchable={false}
          placeholder="Тип"
          valueKey="value"
          labelKey="label"
          size="md"
          onChange={handleFieldChange('type')}
        />
        <CheckPicker
          value={state.model}
          data={getSearchableValues(filtersData?.shopDeviceFilters.deviceModels) ?? []}
          searchable={false}
          placeholder="Модель"
          valueKey="value"
          labelKey="label"
          size="md"
          onChange={handleFieldChange('model')}
        />
        <CheckPicker
          value={state.os}
          data={getSearchableValues(filtersData?.shopDeviceFilters.deviceOs) ?? []}
          searchable={false}
          placeholder="ОС"
          valueKey="value"
          labelKey="label"
          size="md"
          onChange={handleFieldChange('os')}
        />
        <CheckPicker
          value={state.osVersion}
          data={getSearchableValues(filtersData?.shopDeviceFilters.deviceOsVersions) ?? []}
          searchable={false}
          placeholder="Версия ОС"
          valueKey="value"
          labelKey="label"
          size="md"
          onChange={handleFieldChange('osVersion')}
        />
        <CheckPicker
          value={state.appVersion}
          data={getSearchableValues(filtersData?.shopDeviceFilters.deviceAppVersions) ?? []}
          searchable={false}
          placeholder="Версия приложения"
          valueKey="value"
          labelKey="label"
          size="md"
          onChange={handleFieldChange('appVersion')}
        />
        <CheckPicker
          value={state.shopId}
          data={shops}
          onOpen={fetchShops}
          searchable={false}
          placeholder="Магазины"
          valueKey="value"
          labelKey="label"
          size="md"
          style={{ maxWidth: 260 }}
          renderMenu={renderLazyPickerMenu(loading, data)}
          onChange={handleFieldChange('shopId')}
        />
      </Form.Group>

      <div className="ShopsFilter__Controls">
        <Button 
          onClick={handleFormSubmit} 
          size="md" 
          color="green" 
          appearance="primary"
        >
          Поиск
        </Button>
      </div>
    </Form>
  )
}